import { useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { isDropComingSoon } from 'root/src/shared/drops/helpers/isDropComingSoon.helper';
import { useIsAdmin } from 'root/src/client/v2/auth/hooks/useIsAdmin';
import { Switch } from 'root/src/client/v2/common/components/ui/switch';
import { getDropAssetImageUrls } from 'root/src/shared/drops/helpers/dropAsset.helpers';
import { getDateWithTimezone } from 'root/src/shared/util/getDateWithTimezone';
import { RedesignLayout } from 'root/src/client/v2/layout/features/RedesignLayout/RedesignLayout';
import { Toaster } from 'root/src/client/v2/common/components/ui/sonner';
import { buildAssetUrl } from 'root/src/client/v2/common/helpers/assetUrl';
import { upscaleBoxArt } from 'root/src/client/v2/_pages/drops/redesign/dropImage';
import defaultPublisherLogo from 'root/src/client/public/default-publisher-logo.png';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { Skeleton } from 'root/src/client/v2/common/components/ui/skeleton';
import { cn } from 'root/src/client/v2/common/helpers/cn';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import PeopleIcon from '@mui/icons-material/People';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import GroupsIcon from '@mui/icons-material/Groups';
import ChecklistIcon from '@mui/icons-material/Checklist';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import QuizIcon from '@mui/icons-material/Quiz';
import { useDropEvent } from 'root/src/client/v2/_pages/drop/redesign/useDropEvent';
import { DropPreviewBanner } from 'root/src/client/v2/_pages/drop/redesign/DropPreviewBanner';
import { DropCountdown } from 'root/src/client/v2/_pages/drop/redesign/DropCountdown';
import { DropNotifyButton } from 'root/src/client/v2/_pages/drop/redesign/DropNotifyButton';
import { DropSectionNav } from 'root/src/client/v2/_pages/drop/redesign/DropSectionNav';
import { DropOverviewSection } from 'root/src/client/v2/_pages/drop/redesign/DropOverviewSection';
import { DropDetailsCard } from 'root/src/client/v2/_pages/drop/redesign/DropDetailsCard';
import { DropMediaSection } from 'root/src/client/v2/_pages/drop/redesign/DropMediaSection';
import { DropCreatorsSection } from 'root/src/client/v2/_pages/drop/redesign/DropCreatorsSection';
import { DropDaresSection } from 'root/src/client/v2/_pages/drop/redesign/DropDaresSection';
import { RedesignCommunityDaresSection } from 'root/src/client/v2/_pages/drop/redesign/RedesignCommunityDaresSection';
import { DropRequirementsSection } from 'root/src/client/v2/_pages/drop/redesign/DropRequirementsSection';
import { DropHowItWorksSection } from 'root/src/client/v2/_pages/drop/redesign/DropHowItWorksSection';
import { DropFaqSection } from 'root/src/client/v2/_pages/drop/redesign/DropFaqSection';
import { TourProvider } from '@reactour/tour';
import { DropOnboarding } from 'root/src/client/v2/_pages/drop/redesign/dropOnboarding';
import { redesignTourPopoverStyle, redesignTourMaskStyle } from 'root/src/client/v2/onboarding/redesign/redesignTourPopover';
import { Img } from 'root/src/client/v2/common/components/ui/image';
const getDropLabel = (drop) => {
    var _a;
    if (drop.isMultiGame) {
        return drop.campaignProduct;
    }
    return drop.dropTitle || ((_a = drop.gameObj) === null || _a === void 0 ? void 0 : _a.label) || drop.campaignName;
};
// Match the marketplace hero image choice (drops/redesign/dropImage pickHeroImage): the drop's
// first promotional asset, box art only as a fallback, so the same background the user sees on
// the marketplace carries onto the drop detail page.
const pickDropHeroImage = (drop) => {
    var _a, _b;
    return (_a = getDropAssetImageUrls(drop.assets)[0]) !== null && _a !== void 0 ? _a : (_b = drop.gameObj) === null || _b === void 0 ? void 0 : _b.image;
};
const getDropStatus = (drop) => {
    const ended = drop.expirationDate ? new Date(drop.expirationDate).getTime() < Date.now() : false;
    if (ended) {
        return { label: 'Ended', badge: 'border border-solid border-white/20 bg-black/60 text-muted-foreground', dot: 'bg-muted-foreground' };
    }
    if (isDropComingSoon(drop)) {
        return { label: 'Coming Soon', badge: 'border border-solid border-neon-amber/40 bg-black/60 text-neon-amber', dot: 'bg-neon-amber' };
    }
    return { label: 'Live Now', badge: 'border border-solid border-neon-green/40 bg-black/60 text-neon-green', dot: 'bg-neon-green' };
};
// On the preview page (unpublished proposal draft), the drop's real live/coming-soon/ended state is not
// meaningful (a draft has no true live date yet), so the hero shows a neutral "Preview" status instead.
const DROP_PREVIEW_STATUS = { label: 'Preview', badge: 'border border-solid border-neon-purple/40 bg-black/60 text-neon-purple', dot: 'bg-neon-purple' };
// bottom-24 clears the floating Discord widget underneath (same bottom-right stack as the legacy
// DropWidget above DiscordWidget).
const AdminPreviewToggle = ({ comingSoon, onToggle }) => (<div className="fixed bottom-24 right-4 z-50 sm:right-6">
		<label className="flex cursor-pointer select-none items-center gap-2 rounded-xl border border-solid border-white/15 bg-card/90 px-3.5 py-2.5 text-xs font-medium text-muted-foreground shadow-2xl shadow-black/50 backdrop-blur-xl">
			<Switch checked={!comingSoon} onCheckedChange={onToggle} aria-label="Preview the live version"/>
			Live view
		</label>
	</div>);
const DropHero = ({ drop, preview = false }) => {
    var _a, _b, _c, _d, _e;
    // Preview parity with the legacy DropEvent `preview` prop: an unpublished proposal draft is shown as
    // its live experience (never gated behind the coming-soon notify screen or an "ended" state), just
    // under a clear "Preview" badge. The live-only email-notify subscription is suppressed too (it does
    // not apply to an unpublished draft), so the prospect always sees the same "Accept a Dare" CTA.
    const status = preview ? DROP_PREVIEW_STATUS : getDropStatus(drop);
    const heroImage = buildAssetUrl(upscaleBoxArt(pickDropHeroImage(drop)), { w: 1920, q: 85, fmt: 'webp' });
    const brandLogo = (_c = buildAssetUrl((_b = (_a = drop.brand) === null || _a === void 0 ? void 0 : _a.brandLogo) === null || _b === void 0 ? void 0 : _b.small, { w: 160, q: 80, fmt: 'webp' })) !== null && _c !== void 0 ? _c : defaultPublisherLogo.src;
    const comingSoon = isDropComingSoon(drop);
    const ended = drop.expirationDate ? new Date(drop.expirationDate).getTime() < Date.now() : false;
    const countdownTarget = comingSoon ? drop.liveDate : (ended ? undefined : drop.expirationDate);
    const countdownLabel = comingSoon ? 'Starts in' : 'Ends in';
    return (<section className="relative overflow-hidden pt-24" style={{ minHeight: '56vh' }}>
			{heroImage && (<div className="absolute inset-0">
					<Img src={heroImage} alt={getDropLabel(drop)} className="h-full w-full object-cover"/>
					<div className="absolute inset-0 bg-gradient-to-t from-background via-background/75 to-background/30"/>
					<div className="absolute inset-0 bg-gradient-to-r from-background/70 via-transparent to-transparent"/>
				</div>)}

			<div className="relative z-10 mx-auto grid min-h-[44vh] max-w-7xl grid-cols-1 items-end gap-8 px-4 pb-10 sm:px-6 lg:grid-cols-[minmax(0,1fr)_minmax(320px,380px)] lg:gap-12 lg:px-8">
				<div className="max-w-2xl">
					<span className={cn('inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold shadow-sm backdrop-blur-md', status.badge)}>
						<span className={cn('h-2 w-2 rounded-full', status.dot)}/>
						{status.label}
					</span>

					<div className="mt-4 flex items-center gap-2">
						<Img src={brandLogo} alt={(_d = drop.brand) === null || _d === void 0 ? void 0 : _d.brandName} className="h-6 w-6 rounded-full border border-solid border-white/10 object-cover"/>
						<span className="text-sm font-medium text-foreground">{(_e = drop.brand) === null || _e === void 0 ? void 0 : _e.brandName}</span>
					</div>

					<h1 className="mt-3 font-display text-4xl font-extrabold text-foreground sm:text-5xl lg:text-6xl">{getDropLabel(drop)}</h1>
					{drop.gameDescriptionBody && (<p className="mt-4 line-clamp-3 max-w-xl text-sm text-muted-foreground sm:text-base">{drop.gameDescriptionBody}</p>)}

					{countdownTarget && !preview && (<div className="mt-6" data-onboarding="drop-countdown">
							<DropCountdown target={countdownTarget} label={countdownLabel}/>
							{comingSoon && drop.liveDate && (<p className="mt-2 text-xs font-medium text-muted-foreground">Drop starts {getDateWithTimezone(drop.liveDate)}</p>)}
						</div>)}

					{comingSoon && !preview ? (<DropNotifyButton dropId={drop.id} label="Notify me" className="mt-8 rounded-lg px-8 text-sm font-medium"/>) : (<Button variant="brand" className="gleam mt-8 rounded-lg px-8 text-sm font-medium" asChild>
							<a href="#dares">Accept a Dare</a>
						</Button>)}
				</div>

				<div className="w-full lg:justify-self-end">
					<DropDetailsCard drop={drop}/>
				</div>
			</div>
		</section>);
};
const DropHeroSkeleton = () => (<section className="relative overflow-hidden pt-24" style={{ minHeight: '56vh' }}>
		<Skeleton className="absolute inset-0 h-full w-full rounded-none"/>
		<div className="relative z-10 mx-auto grid min-h-[44vh] max-w-7xl grid-cols-1 items-end gap-8 px-4 pb-10 sm:px-6 lg:grid-cols-[minmax(0,1fr)_minmax(320px,380px)] lg:gap-12 lg:px-8">
			<div className="w-full max-w-2xl space-y-4">
				<Skeleton className="h-7 w-24 rounded-full"/>
				<Skeleton className="h-12 w-80"/>
				<Skeleton className="h-4 w-full max-w-xl"/>
				<Skeleton className="h-10 w-40 rounded-lg"/>
			</div>
			<Skeleton className="h-72 w-full rounded-2xl lg:justify-self-end"/>
		</div>
	</section>);
export const RedesignDropPage = ({ preview = false }) => {
    const router = useRouter();
    const mnemonicId = typeof router.query.recordId === 'string' ? router.query.recordId : undefined;
    const { drop, isLoading, isError, refetch } = useDropEvent(mnemonicId, preview);
    const rawDropEvent = drop;
    const { isAdmin } = useIsAdmin();
    // Admin preview of the other live/coming-soon variant (legacy DropWidget parity). Instead of
    // threading an override flag into every child, shift `liveDate` on a copy of the drop: every
    // component that derives coming-soon via isDropComingSoon(drop) then agrees, the same trick the
    // legacy page pulled with its customDate. undefined = no override, show reality.
    const [previewComingSoon, setPreviewComingSoon] = useState(undefined);
    const actualComingSoon = rawDropEvent ? isDropComingSoon(rawDropEvent) : false;
    const previewedComingSoon = previewComingSoon !== null && previewComingSoon !== void 0 ? previewComingSoon : actualComingSoon;
    const dropEvent = useMemo(() => {
        if (!rawDropEvent || previewComingSoon === undefined || previewComingSoon === actualComingSoon) {
            return rawDropEvent;
        }
        const shiftedLiveDate = previewComingSoon
            ? new Date(Date.now() + 14 * 24 * 3600 * 1000).toISOString()
            : new Date(Date.now() - 60 * 1000).toISOString();
        return Object.assign(Object.assign({}, rawDropEvent), { liveDate: shiftedLiveDate });
    }, [rawDropEvent, previewComingSoon, actualComingSoon]);
    return (<RedesignLayout>
			{preview && <DropPreviewBanner />}
			{isError && (<div className="flex min-h-[60vh] items-center justify-center px-4 pt-24 text-center text-muted-foreground">
					This drop could not be found.
				</div>)}
			{!isError && (isLoading || !dropEvent) && <DropHeroSkeleton />}
			{!isError && !isLoading && dropEvent && (() => {
            var _a, _b, _c, _d;
            const hasDares = ((_b = (_a = dropEvent.dares) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0;
            const hasMedia = ((_d = (_c = dropEvent.assets) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0) > 0 || Boolean(dropEvent.youtubeURL);
            const hasCommunityDares = Boolean(dropEvent.enableCreatorDares);
            const sections = [
                { id: 'overview', label: 'Overview', Icon: VisibilityIcon },
                ...(hasMedia ? [{ id: 'media', label: 'Media', Icon: PlayCircleIcon }] : []),
                { id: 'creators', label: 'Creators', Icon: PeopleIcon },
                ...(hasDares ? [{ id: 'dares', label: 'Dares', Icon: SportsEsportsIcon }] : []),
                ...(hasCommunityDares ? [{ id: 'community-dares', label: 'Community', Icon: GroupsIcon }] : []),
                { id: 'requirements', label: 'Requirements', Icon: ChecklistIcon },
                { id: 'howto', label: 'How It Works', Icon: HelpOutlineIcon },
                { id: 'faq', label: 'FAQ', Icon: QuizIcon },
            ];
            return (<TourProvider steps={[]} showBadge={false} showCloseButton={false} showNavigation={false} styles={{ popover: redesignTourPopoverStyle, maskWrapper: redesignTourMaskStyle }}>
						<DropHero drop={dropEvent} preview={preview}/>
						{isAdmin && (<AdminPreviewToggle comingSoon={previewedComingSoon} onToggle={() => setPreviewComingSoon(!previewedComingSoon)}/>)}
						<DropSectionNav sections={sections}/>
						<DropOverviewSection drop={dropEvent}/>
						{hasMedia && <DropMediaSection drop={dropEvent}/>}
						<DropCreatorsSection mnemonicId={mnemonicId !== null && mnemonicId !== void 0 ? mnemonicId : ''}/>
						{hasDares && <DropDaresSection drop={dropEvent} refetchDropEvent={refetch}/>}
						{hasCommunityDares && <RedesignCommunityDaresSection dropId={dropEvent.id} enableCreatorDares={hasCommunityDares}/>}
						<DropRequirementsSection drop={dropEvent}/>
						<DropHowItWorksSection />
						<DropFaqSection />
						<DropOnboarding />
					</TourProvider>);
        })()}
			<Toaster />
		</RedesignLayout>);
};
